import {
  PROVIDER_IDENTIFICATION_LABELS,
  PROVIDER_IDENTIFICATION_PROVIDERS,
} from 'shared/constants/quality-metric.constants';

/** The provider a name typed in the drawer gets until the admin picks another. */
export const DEFAULT_PROVIDER = PROVIDER_IDENTIFICATION_PROVIDERS.MPOG;

/** A stored identification as user reads return it; `key` is server-derived and never sent back. */
export interface ProviderIdentification {
  provider: string;
  value: string;
  key?: string;
}

/** One name in the drawer's list: the provider that reports it and the name as typed. */
export interface ProviderName {
  provider: string;
  value: string;
}

interface ProviderNamesFormValues {
  providerNames?: ProviderName[];
  providerIdentifications?: ProviderIdentification[] | null;
}

/**
 * The form names are compared on: trimmed, inner whitespace collapsed, upper-cased — the
 * backend's `key` rule, so `"Roe,  richard"` and `"ROE, RICHARD"` are the same name.
 */
export const normalizeProviderKey = (value: string): string =>
  value.trim().replace(/\s+/g, ' ').toUpperCase();

/**
 * Identity of a name in the list: the same name under two providers is two entries.
 *
 * @param entry - A provider name.
 * @returns `<provider>:<normalized key>`.
 */
export const providerNameId = ({ provider, value }: ProviderName): string =>
  `${provider}:${normalizeProviderKey(value)}`;

/**
 * Display name of a provider, falling back to the raw key for one this client does not know.
 *
 * @param provider - The provider key (`mpog`, `keck`, …).
 * @returns The label.
 */
export const getProviderLabel = (provider: string): string =>
  (PROVIDER_IDENTIFICATION_LABELS as Record<string, string>)[provider] ?? provider;

/**
 * Every name a user carries for one provider, in stored order.
 *
 * @param identifications - `user.providerIdentifications` as read from the API.
 * @param provider - The provider to read.
 * @returns The `value`s; `[]` when there are none.
 */
export const getProviderNames = (
  identifications: ProviderIdentification[] | null | undefined,
  provider: string,
): string[] =>
  (identifications ?? []).filter(entry => entry.provider === provider).map(({ value }) => value);

/**
 * Cleans a list of names: trims each, drops empty ones, and keeps the first spelling of names
 * of one provider that only differ in case or spacing. Takes the read array too (drops `key`).
 *
 * @param names - The names, as typed or as read.
 * @returns The cleaned list, in the given order.
 */
export const toProviderNamesList = (
  names: ProviderName[] | ProviderIdentification[] | null | undefined,
): ProviderName[] => {
  const seen = new Set<string>();
  return (names ?? []).flatMap(({ provider, value }) => {
    const trimmed = (value ?? '').trim();
    const id = providerNameId({ provider, value: trimmed });
    if (!trimmed || seen.has(id)) return [];
    seen.add(id);
    return [{ provider, value: trimmed }];
  });
};

/**
 * Adds a typed name to the drawer's list. Appends only — existing names are kept, and a name
 * the list already holds for that provider in another case or spacing is not added twice.
 *
 * @param names - The current list.
 * @param value - The typed name.
 * @param provider - The provider picked for it.
 * @returns The new list.
 */
export const addProviderNameToList = (
  names: ProviderName[] | null | undefined,
  value: string,
  provider: string,
): ProviderName[] => toProviderNamesList([...(names ?? []), { provider, value }]);

const sameNames = (a: ProviderName[], b: ProviderName[]) => {
  const ids = (names: ProviderName[]) => names.map(providerNameId).sort().join('\n');
  return ids(a) === ids(b);
};

/**
 * Turns the edit form's `providerNames` into the `PUT /v1/user/:id` field. The form spreads the
 * whole user, so the read array (with `key`) is never sent back as is. The field goes out only
 * when a name changed (compared per provider, normalized), as the full list, and `[]` once
 * every name is removed.
 *
 * @param values - The form values; `providerNames` is the edited list.
 * @param initialNames - The names the form opened with.
 * @returns The payload, without `providerNames`.
 * @example toProviderIdentificationsPayload({ providerNames: [{ provider: 'mpog', value: 'ROE, RICHARD' }, { provider: 'keck', value: ' Roe R ' }] }, [{ provider: 'mpog', value: 'ROE, RICHARD' }])
 * // → { providerIdentifications: [{ provider: 'mpog', value: 'ROE, RICHARD' }, { provider: 'keck', value: 'Roe R' }] }
 */
export const toProviderIdentificationsPayload = <
  T extends ProviderNamesFormValues & Record<string, unknown>,
>(
  values: T,
  initialNames: ProviderName[] = [],
): Omit<T, 'providerNames' | 'providerIdentifications'> & {
  providerIdentifications?: ProviderName[];
} => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { providerNames, providerIdentifications: _stored, ...rest } = values;
  if (providerNames === undefined) {
    return rest;
  }

  const names = toProviderNamesList(providerNames);
  if (sameNames(names, toProviderNamesList(initialNames))) {
    return rest;
  }
  return { ...rest, providerIdentifications: names };
};

/**
 * Adds one name to a user's stored identifications without dropping any: every existing entry
 * is kept (without `key`), and a name the user already has for that provider is not added twice.
 *
 * @param identifications - The user's current, complete identifications.
 * @param value - The name to add.
 * @param provider - The provider that reports the name.
 * @returns The full array for `PUT /v1/user/:id`.
 */
export const appendProviderName = (
  identifications: ProviderIdentification[] | null | undefined,
  value: string,
  provider: string,
): ProviderName[] => {
  const existing = (identifications ?? []).map(entry => ({
    provider: entry.provider,
    value: entry.value,
  }));
  const id = providerNameId({ provider, value });
  return existing.some(entry => providerNameId(entry) === id)
    ? existing
    : [...existing, { provider, value: value.trim() }];
};
